
import { Box, Button, CircularProgress, Grid, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import * as yup from "yup";
import TextFieldInput from "../../../components/TextFieldInput";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import {
  fetchCompanyDetails,
  resetGstDetailsData,
  setActiveStep,
  updateCompanyDetails,
  getApplicantDetails,
  setEditPreviewApplicant,
  setShowSaveAndPreview,
} from "../new-booking-form.slice";
import { useDispatch, useSelector } from "react-redux";
import warningIcon from "../../../assets/icons/ic-solar_warning-triangle-bold.svg";
import CommonButton from "../../../shared-components/common-button/CommonButton";
import { BOOKING_AS } from "../../../config/constant";
import { useHistory, useParams } from "react-router-dom";
import SnackbarAlert from "../../../shared-components/snackbar-alert/SnackbarAlert";
import { route } from "../../../services/apiRoutes";
import { getErrorMessage, getLastStep } from "../../../utilis/common-funtions";
import Loader from "../../../components/Loader";

const CompanyDetails = () => {
  const dispatch = useDispatch();
  const { oppId } = useParams();
  const { t } = useTranslation();
  const history = useHistory();
  const {
    isLoading,
    gstDetailsData,
    activeStep,
    applicantData,
    entity,
    isCorporateFlow,
  } = useSelector((state) => state?.newBookingForm);
  const [snackbarConfig, setSnackbarConfig] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const [isButtonLoading, setIsButtonLoading] = useState(false);
  const [isPanEditable, setIsPanEditable] = useState(false);
  const companyDetailsFormik = useFormik({
    initialValues: {
      gstNumber: "",
      companyPanNumber: "",
      companyName: "",
      companyAddress: "",
      fetchedDetails: false,
    },
    validationSchema: yup.object().shape({
      gstNumber: yup
        .string()
        .required(t("companyDetails.validation.gstNumber", { entity }))
        .matches(
          /^\d{2}[A-Z]{5}\d{4}[A-Z][0-9A-Z]Z[0-9A-Z]$/,
          t("companyDetails.validation.matchGstNumber")
        ),
      companyPanNumber: yup
        .string()
        .required(t("companyDetails.validation.companyPanNumber", { entity }))
        .matches(
          /^[A-Z]{5}\d{4}[A-Z]$/,
          t("companyDetails.validation.matchCompanyPanNumber")
        ),
      companyName: yup
        .string()
        .required(t("companyDetails.validation.companyName", { entity })),
      companyAddress: yup
        .string()
        .required(t("companyDetails.validation.companyAddress", { entity })),
    }),
    onSubmit: (values) => { },
  });

  useEffect(() => {
    companyDetailsFormik.validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyDetailsFormik?.values]);


  useEffect(() => {
    if (applicantData?.companyDetails) {
      const pan = applicantData?.companyDetails?.companyPan || "";
      setIsPanEditable(pan === "[REDACTED]");
      companyDetailsFormik.setValues({
        gstNumber: applicantData?.companyDetails?.gstNumber || "",
        companyPanNumber: pan,
        companyName: applicantData?.companyDetails?.companyName || "",
        companyAddress: applicantData?.companyDetails?.companyAddress || "",
        fetchedDetails: true,
      });
    } else {
      companyDetailsFormik.resetForm();
      setIsPanEditable(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicantData]);

  const handleFetchCompanyDetails = async () => {
    // Validate only GST field
    await companyDetailsFormik.validateField("gstNumber");
    companyDetailsFormik.setFieldTouched("gstNumber", true, false);

    // Stop if invalid
    if (companyDetailsFormik.errors.gstNumber) {
      return;
    }

    setIsButtonLoading(true);
    try {
      const payload = {
        gstNumber: companyDetailsFormik?.values?.gstNumber || "",
        opportunityId: oppId,
        bookingAs: isCorporateFlow
          ? BOOKING_AS["CORPORATE"]
          : BOOKING_AS["PARTNERSHIP_FIRM"],
      };
      const res = await dispatch(fetchCompanyDetails(payload)).unwrap();

      if (res?.kycResult) {
        const pan = res?.kycResult?.pan || "";
        setIsPanEditable(pan === "[REDACTED]");
        companyDetailsFormik.setValues({
          gstNumber: res?.kycResult?.gstin || "",
          companyPanNumber: pan,
          companyName: res?.kycResult?.tradeName || "",
          companyAddress: res?.kycResult?.principalPlaceOfBusiness || "",
        });
      }
    } catch (err) {
      console.error("Failed to fetch company details:", err);
      setSnackbarConfig({
        open: true,
        message: getErrorMessage(err),
        severity: "error",
      });
    } finally {
      setIsButtonLoading(false);
    }
  };

  const createCompanyDetailsPayload = (saveForLaterFlag = false) => {
    return {
      opportunityId: oppId,
      lastStep: getLastStep(applicantData?.lastStep, activeStep),
      bookingAs: applicantData?.bookingAs,
      gstNumber: companyDetailsFormik?.values?.gstNumber,
      companyName: companyDetailsFormik?.values?.companyName,
      companyPan: companyDetailsFormik?.values?.companyPanNumber,
      companyAddress: companyDetailsFormik?.values?.companyAddress,
    };
  };

  const handlePrevious = () => {
    globalThis.location.reload();
  };

  const handleSaveForm = async (isSaveandPreview = false) => {
    await companyDetailsFormik.validateForm();
    if (Object.keys(companyDetailsFormik.errors).length === 0) {
      const payload = createCompanyDetailsPayload(false);

      try {
        const res = await dispatch(updateCompanyDetails({ payload })).unwrap();
        if (res) {
          dispatch(getApplicantDetails(`/${oppId}`));
        }
        dispatch(setActiveStep(activeStep + 1));
        dispatch(setShowSaveAndPreview(false));
        if (isSaveandPreview) {
          dispatch(setEditPreviewApplicant(false));
          history.push(`/customer-preview/${oppId}`);
        }
        // Optionally navigate to next step
      } catch (err) {
        console.error("Booking updation failed:", err);
        setSnackbarConfig({
          open: true,
          message: getErrorMessage(err),
          severity: "error",
        });
      }
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarConfig({
      open: false,
      message: "",
      severity: "",
    });
  };

  let buttonContent;
  if (isButtonLoading) {
    buttonContent = <CircularProgress size={24} color="inherit" />;
  } else if (applicantData?.bookingAs === BOOKING_AS.CORPORATE) {
    buttonContent = t("companyDetails.fetchCompanyDetailsCorporate");
  } else {
    buttonContent = t("companyDetails.fetchCompanyDetailsIndividual");
  }

  return (
    <div>
      {isLoading && <Loader isLoading={isLoading} />}
      <Typography
        sx={{
          color: "#1C252E",
          fontSize: "16px",
          fontWeight: "600",
          lineHeight: "36px",
          mb: "12px",
          textAlign: "center",
        }}
      >
        {t("companyDetails.title", { entity })}
      </Typography>
      <form
        onSubmit={companyDetailsFormik.handleSubmit}
        onReset={companyDetailsFormik.handleReset}
        autoComplete="off"
      >
        <Grid container spacing={1} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={4} md={4} lg={4} xl={4}>
            <TextFieldInput
              field={{
                name: "gstNumber",
                placeholder: t("companyDetails.placeholder.gstNumber", {
                  entity,
                }),
                label: t("companyDetails.labels.gstNumber"),
                required: true,
                readonly: false,
                showClearIcon: true,
                onClear: () => {
                  // Clear dependent fields
                  companyDetailsFormik.setValues({
                    gstNumber: "",
                    companyPanNumber: "",
                    companyName: "",
                    companyAddress: "",
                  });
                  setIsPanEditable(false);
                  // Reset gstDetailsData in Redux
                  dispatch(resetGstDetailsData());
                },
              }}
              formik={companyDetailsFormik}
            />
            {gstDetailsData?.kycStatus === "FAILURE" ? (
              <Box sx={{ display: "flex", mt: 0.5 }}>
                <img src={warningIcon} alt="warning-icon" />
                <Typography
                  sx={{ fontSize: "12px", color: "#FF5630", ml: 0.5 }}
                >
                  {t("companyDetails.validation.invalidGstNumber", {
                    entity: entity?.toLowerCase(),
                  })}
                </Typography>
              </Box>
            ) : null}
          </Grid>
          <Grid item xs={12} sm={4} md={4} lg={4} xl={4} mt={3.5}>
            <Button
              variant="contained"
              size="large"
              onClick={handleFetchCompanyDetails}
              disabled={
                companyDetailsFormik?.values?.fetchedDetails ||
                !companyDetailsFormik.values.gstNumber || // disable if GST is empty
                !!gstDetailsData?.kycResult // or if already fetched
              }
              sx={{
                backgroundColor: "#1A407D",
                textTransform: "none",
                fontSize: "14px",
                borderRadius: "8px",
                fontWeight: 400,
                "&:hover": {
                  backgroundColor: "#1A407D",
                },
                py: 1.25,
                minWidth: "200px",
              }}
            >
              {buttonContent}
            </Button>
          </Grid>
        </Grid>

        {companyDetailsFormik.values.companyPanNumber ||
          companyDetailsFormik.values.companyName ||
          companyDetailsFormik.values.companyAddress ? (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={5} md={5} lg={6} xl={6}>
              <TextFieldInput
                field={{
                  name: "companyPanNumber",
                  placeholder: t(
                    "companyDetails.placeholder.companyPanNumber",
                    { entity }
                  ),
                  label: t("companyDetails.labels.companyPanNumber", {
                    entity,
                  }),
                  required: true,
                  readonly: !isPanEditable,
                }}
                formik={companyDetailsFormik}
              />
            </Grid>
            <Grid item xs={12} sm={5} md={5} lg={6} xl={6}>
              <TextFieldInput
                field={{
                  name: "companyName",
                  placeholder: t("companyDetails.placeholder.companyName", {
                    entity,
                  }),
                  label: t("companyDetails.labels.companyName", { entity }),
                  required: true,
                  readonly: true,
                }}
                formik={companyDetailsFormik}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <TextFieldInput
                field={{
                  name: "companyAddress",
                  placeholder: t("companyDetails.placeholder.companyAddress", {
                    entity,
                  }),
                  label: t("companyDetails.labels.companyAddress", { entity }),
                  required: true,
                  readonly: true,
                  multiline: true,
                  rows: 4,
                }}
                formik={companyDetailsFormik}
              />
            </Grid>
          </Grid>
        ) : null}
        <CommonButton
          handlePrevious={handlePrevious}
          handleNext={() => {
            handleSaveForm(false);
          }}
          innerSteps={true}
          saveForLater={false}
          saveAndPreview={() => {
            handleSaveForm(true);
          }}
          // applicantNo={applicantNo}
          saveDraftData={{
            ...createCompanyDetailsPayload(true),
          }}
          saveDraftPath={route.UPDATE_COMPANY_DETAILS}
          showPrevBtn={true}
        />
      </form>
      <SnackbarAlert
        open={snackbarConfig.open}
        message={snackbarConfig.message}
        severity={snackbarConfig.severity}
        onClose={handleSnackbarClose}
      />
    </div>
  );
};

export default CompanyDetails;
